import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { goHome } from '../utils/navigation';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
  fallbackMessage?: string;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  fallbackMessage,
  allowedRoles = []
}) => {
  const { user } = useAuth();

  // Check if user is authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-24 w-24 object-contain"
              onError={(e) => e.currentTarget.style.display = 'none'}
            />
          </div>
          <Shield className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h2 className="font-runtime text-2xl font-bold text-black mb-2">
            Authentication Required
          </h2>
          <p className="font-lucy-rose text-gray-600 mb-6">
            Please sign in to access this page.
          </p>
          <div className="space-y-3">
            <button
              onClick={goHome}
              className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Go to Login
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full flex items-center justify-center space-x-2 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-lucy-rose font-medium text-gray-700"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Go Back</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check role permissions
  if (requiredRole) {
    const requiredRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasRequiredRole = requiredRoles.includes(user.role);
    
    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-24 w-24 object-contain"
              onError={(e) => e.currentTarget.style.display = 'none'}
            />
          </div>
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="font-runtime text-2xl font-bold text-black mb-2">
            Access Denied
          </h2>
            <p className="font-lucy-rose text-gray-600 mb-2">
              {fallbackMessage || `You need ${requiredRoles.join(' or ')} privileges to access this page.`}
            </p>
            <p className="font-lucy-rose text-sm text-gray-500 mb-6">
              Current role: {user.role}
            </p>
            <div className="space-y-3">
              <button
                onClick={goHome}
                className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Go to Home
              </button>
              <button
                onClick={() => window.history.back()}
                className="w-full flex items-center justify-center space-x-2 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-lucy-rose font-medium text-gray-700"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Go Back</span>
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Check allowed roles (alternative permission check)
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-24 w-24 object-contain"
              onError={(e) => e.currentTarget.style.display = 'none'}
            />
          </div>
          <Shield className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h2 className="font-runtime text-2xl font-bold text-black mb-2">
            Insufficient Permissions
          </h2>
          <p className="font-lucy-rose text-gray-600 mb-2">
            This page is restricted to specific user roles.
          </p>
          <p className="font-lucy-rose text-sm text-gray-500 mb-6">
            Allowed roles: {allowedRoles.join(', ')}
          </p>
          <button
            onClick={goHome}
            className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;